import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowLeftRight, GitCompareArrows } from "lucide-react";
import AlignmentUnavailable from "@/components/lapComparison/AlignmentUnavailable";
import ComparisonChannelPicker from "@/components/lapComparison/ComparisonChannelPicker";
import ComparisonChart from "@/components/lapComparison/ComparisonChart";
import ComparisonTrackMap from "@/components/lapComparison/ComparisonTrackMap";
import LapPickerModal from "@/components/lapComparison/LapPickerModal";
import LapSlotCard, { type LapSlotFallback } from "@/components/lapComparison/LapSlotCard";
import SectionReadout from "@/components/lapComparison/SectionReadout";
import {
  defaultChannelKeys,
  formatSignedSeconds,
  sanitizeChannelKeys,
  vehicleLabel,
} from "@/components/lapComparison/comparisonMath";
import { useLapComparison } from "@/hooks/useLapComparison";
import { useSession } from "@/hooks/useSessions";
import { useTelemetryLaps } from "@/hooks/useTelemetry";
import type { LapComparisonChannel, LapComparisonRole, LapRef } from "@/types";
import { formatLapRef, parseLapRef } from "@/utils/lapRef";
import { formatUserName } from "@/utils/userName";

type ComparisonErrorKind =
  | "forbidden"
  | "notFound"
  | "lapNotComplete"
  | "trackMismatch"
  | "generic";

interface ClassifiedError {
  kind: ComparisonErrorKind;
  /** Slot the backend blamed, when it said. */
  role?: LapComparisonRole;
}

function classifyError(error: unknown): ClassifiedError {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { error?: { code?: string; details?: { role?: string } } };
      };
    }
  )?.response;
  const code = response?.data?.error?.code;
  const roleValue = response?.data?.error?.details?.role;
  const role: LapComparisonRole | undefined =
    roleValue === "reference" || roleValue === "comparison" ? roleValue : undefined;
  const kind: ComparisonErrorKind =
    code === "LAP_NOT_COMPLETE"
      ? "lapNotComplete"
      : code === "TRACK_MISMATCH"
        ? "trackMismatch"
        : response?.status === 403
          ? "forbidden"
          : response?.status === 404
            ? "notFound"
            : "generic";
  return role ? { kind, role } : { kind };
}

export default function LapComparisonPage() {
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const refLap = useMemo(() => parseLapRef(searchParams.get("ref")), [searchParams]);
  const cmpLap = useMemo(() => parseLapRef(searchParams.get("cmp")), [searchParams]);
  const urlChannels = useMemo(
    () => (searchParams.get("ch") ?? "").split(",").filter(Boolean),
    [searchParams],
  );

  const [pickerRole, setPickerRole] = useState<LapComparisonRole | null>(null);
  const [cursorIndex, setCursorIndex] = useState<number | null>(null);
  const [selection, setSelection] = useState<[number, number] | null>(null);

  // Context for the reference slot (and the picker's track) before — or
  // without — a comparison response.
  const refSessionQuery = useSession(refLap?.sessionId ?? "");
  const refLapsQuery = useTelemetryLaps(refLap?.sessionId ?? "", Boolean(refLap));
  const refSession = refSessionQuery.data;
  const trackId = refSession?.event.track.id ?? "";
  const trackName = refSession?.event.track.name;

  const comparison = useLapComparison(
    refLap ? formatLapRef(refLap) : null,
    cmpLap ? formatLapRef(cmpLap) : null,
    urlChannels,
  );
  const data = comparison.data;

  // Resolve the channels to show: URL keys the response knows, else defaults.
  const channelByKey = useMemo(() => {
    const map = new Map<string, LapComparisonChannel>();
    for (const channel of data?.channels ?? []) map.set(channel.key, channel);
    return map;
  }, [data?.channels]);
  const selectedKeys = useMemo(() => {
    if (!data) return urlChannels;
    const known = sanitizeChannelKeys(urlChannels, data.channels);
    return known.length > 0 ? known : defaultChannelKeys(data.channels);
  }, [data, urlChannels]);
  const selectedChannels = useMemo(
    () => selectedKeys.map((key) => channelByKey.get(key)).filter((c): c is LapComparisonChannel => Boolean(c)),
    [selectedKeys, channelByKey],
  );

  const updateUrl = useCallback(
    (next: { ref?: LapRef | null; cmp?: LapRef | null; channels?: string[] }, replace: boolean) => {
      const params = new URLSearchParams();
      const nextRef = next.ref === undefined ? refLap : next.ref;
      const nextCmp = next.cmp === undefined ? cmpLap : next.cmp;
      const nextChannels = next.channels ?? urlChannels;
      if (nextRef) params.set("ref", formatLapRef(nextRef));
      if (nextCmp) params.set("cmp", formatLapRef(nextCmp));
      if (nextChannels.length > 0) params.set("ch", nextChannels.join(","));
      setSearchParams(params, { replace });
    },
    [refLap, cmpLap, urlChannels, setSearchParams],
  );

  // Keep `ch` in step with what is actually shown (defaults applied, unknown
  // keys dropped, cap enforced) so the URL stays shareable.
  useEffect(() => {
    if (data && selectedKeys.length > 0 && selectedKeys.join(",") !== urlChannels.join(",")) {
      updateUrl({ channels: selectedKeys }, true);
    }
  }, [data, urlChannels, selectedKeys, updateUrl]);

  // Open the picker automatically when the comparison slot is empty.
  useEffect(() => {
    if (refLap && !cmpLap && pickerRole === null && trackId) {
      setPickerRole("comparison");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refLap?.sessionId, refLap?.lapNumber, cmpLap, trackId]);

  // Drop cursor/selection when the lap pair changes.
  useEffect(() => {
    setCursorIndex(null);
    setSelection(null);
  }, [refLap?.sessionId, refLap?.lapNumber, cmpLap?.sessionId, cmpLap?.lapNumber]);

  const handleSwap = useCallback(() => {
    if (!refLap || !cmpLap) return;
    updateUrl({ ref: cmpLap, cmp: refLap }, false);
  }, [refLap, cmpLap, updateUrl]);

  // Filling an empty slot rewrites the current entry (Back returns to the
  // analytics page, not to the half-filled picker); replacing a lap pushes
  // so the previous pair stays reachable.
  const handlePick = useCallback(
    (lap: LapRef) => {
      if (pickerRole === "reference") updateUrl({ ref: lap }, !refLap);
      else updateUrl({ cmp: lap }, !cmpLap);
    },
    [pickerRole, refLap, cmpLap, updateUrl],
  );

  const closePicker = useCallback(() => setPickerRole(null), []);

  const channelLabel = useCallback(
    (channel: LapComparisonChannel) => {
      const key = `pages:lapComparison.channelLabels.${channel.key}`;
      const translated = t(key, { defaultValue: "" });
      return translated || channel.label;
    },
    [t],
  );

  const referenceFallback = useMemo<LapSlotFallback | undefined>(() => {
    if (!refLap) return undefined;
    const lap = refLapsQuery.data?.data.laps.find((l) => l.lapNumber === refLap.lapNumber);
    return {
      sessionTitle: refSession?.title,
      driverName: refSession ? formatUserName(refSession.user) : undefined,
      vehicleName: refSession?.vehicle
        ? vehicleLabel({
            name: refSession.vehicle.name ?? null,
            year: refSession.vehicle.year,
            maker: refSession.vehicle.maker,
            model: refSession.vehicle.model,
          })
        : undefined,
      conductedAt: refSession?.conductedAt,
      lapNumber: refLap.lapNumber,
      lapTimeMs: lap ? Math.round(lap.lapTime * 1000) : undefined,
    };
  }, [refLap, refSession, refLapsQuery.data]);

  const comparisonFallback = useMemo<LapSlotFallback | undefined>(
    () => (cmpLap ? { lapNumber: cmpLap.lapNumber } : undefined),
    [cmpLap],
  );

  const referenceLabel = data
    ? `${data.reference.session.driver.name} · L${data.reference.lapNumber}`
    : t("pages:lapComparison.reference");
  const comparisonLabel = data
    ? `${data.comparison.session.driver.name} · L${data.comparison.lapNumber}`
    : t("pages:lapComparison.comparison");

  const backTarget = refLap ? `/sessions/${refLap.sessionId}/analytics` : "/sessions";
  // A reference session the viewer cannot read (shared URL) surfaces here
  // even before a comparison lap is chosen.
  const error = comparison.isError
    ? classifyError(comparison.error)
    : refSessionQuery.isError
      ? { ...classifyError(refSessionQuery.error), role: "reference" as const }
      : null;
  const pickerUnavailable = !trackId;
  const readoutId = "lap-comparison-readout";

  return (
    <div className="dvh-fill flex min-h-0 flex-col overflow-hidden bg-[#0d0d14] text-slate-200">
      <header className="flex-shrink-0 border-b border-[#1a1a2e] bg-[#0a0a0f] px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backTarget)}
            aria-label={t("common:back")}
            className="rounded-lg p-2 text-[#4b5563] transition-colors hover:bg-[#111827] hover:text-[#94a3b8]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 truncate text-base font-semibold text-slate-200">
              <GitCompareArrows className="h-4 w-4 text-[#4b6bfb]" />
              {t("pages:lapComparison.title")}
              {trackName && <span className="truncate text-sm font-normal text-slate-500">· {trackName}</span>}
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">{t("pages:lapComparison.subtitle")}</p>
          </div>
          {data?.alignment.status === "ok" && (
            <div className="hidden text-right text-xs text-slate-500 sm:block">
              <div>
                {t("pages:lapComparison.lapTimeDelta")}{" "}
                <span
                  className={`font-mono font-semibold ${
                    data.alignment.quality.lapTimeDeltaMs > 0
                      ? "text-red-300"
                      : data.alignment.quality.lapTimeDeltaMs < 0
                        ? "text-green-300"
                        : "text-slate-200"
                  }`}
                >
                  {formatSignedSeconds(data.alignment.quality.lapTimeDeltaMs / 1000)}
                </span>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-4 px-4 py-4 sm:px-5">
          <section className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center">
            <LapSlotCard
              role="reference"
              lap={data?.reference ?? null}
              fallback={referenceFallback}
              disabled={pickerUnavailable}
              onReplace={() => setPickerRole("reference")}
            />
            <button
              type="button"
              onClick={handleSwap}
              disabled={!refLap || !cmpLap}
              aria-label={t("pages:lapComparison.swap")}
              title={t("pages:lapComparison.swap")}
              className="self-center rounded-full border border-[#1f2937] p-2 text-slate-300 transition-colors hover:bg-[#111827] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
            <LapSlotCard
              role="comparison"
              lap={data?.comparison ?? null}
              fallback={comparisonFallback}
              deltaMs={data?.alignment.status === "ok" ? data.alignment.quality.lapTimeDeltaMs : null}
              disabled={pickerUnavailable}
              onReplace={() => setPickerRole("comparison")}
            />
          </section>

          {error ? (
            <div
              className="flex items-start gap-3 rounded-xl border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200"
              role="alert"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                {t(`pages:lapComparison.errors.${error.kind}`)}
                {error.role && (
                  <span className="text-red-300/80">
                    {" "}
                    ({t(`pages:lapComparison.${error.role}`)})
                  </span>
                )}
              </div>
            </div>
          ) : !refLap ? (
            <div className="rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-8 text-center text-sm text-slate-400">
              {t("pages:lapComparison.needReference")}
            </div>
          ) : !cmpLap ? (
            <div className="rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-8 text-center text-sm text-slate-400">
              {t("pages:lapComparison.needComparison")}
            </div>
          ) : comparison.isLoading && !data ? (
            <div className="rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-8 text-center text-sm text-slate-400">
              {t("pages:lapComparison.loading")}
            </div>
          ) : data && data.alignment.status === "unavailable" ? (
            <AlignmentUnavailable alignment={data.alignment} />
          ) : data && data.series && data.alignment.status === "ok" ? (
            <>
              {!data.alignment.quality.finishDeltaConsistent && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200" role="status">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {t("pages:lapComparison.quality.inconsistent", {
                    finish: formatSignedSeconds(data.alignment.quality.finishDeltaMs / 1000),
                    lapTime: formatSignedSeconds(data.alignment.quality.lapTimeDeltaMs / 1000),
                  })}
                </div>
              )}
              <section
                className={`grid gap-4 transition-opacity lg:grid-cols-[220px_minmax(0,1fr)] ${
                  comparison.isPlaceholderData ? "opacity-70" : ""
                }`}
                aria-busy={comparison.isPlaceholderData}
              >
                <aside className="max-h-64 rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-3 lg:max-h-[70vh]">
                  <ComparisonChannelPicker
                    channels={data.channels}
                    selectedKeys={selectedKeys}
                    onChange={(keys) => updateUrl({ channels: keys }, true)}
                    channelLabel={channelLabel}
                  />
                </aside>
                <div className="min-w-0 space-y-4">
                  <div className="overflow-hidden rounded-xl border border-[#1a1a2e]">
                    <ComparisonChart
                      series={data.series}
                      channels={selectedChannels}
                      channelLabel={channelLabel}
                      deltaLabel={t("pages:lapComparison.chart.delta")}
                      notRecordedLabel={t("pages:lapComparison.notRecorded")}
                      cursorIndex={cursorIndex}
                      onCursorChange={setCursorIndex}
                      selection={selection}
                      onSelectionChange={setSelection}
                      ariaDescribedBy={readoutId}
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-[minmax(260px,440px)_minmax(0,1fr)]">
                    <div className="rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-3">
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t("pages:lapComparison.map.title")}
                      </h3>
                      <ComparisonTrackMap
                        referenceTrack={data.series.referenceTrack}
                        comparisonTrack={data.series.comparisonTrack}
                        cursorIndex={cursorIndex}
                        selection={selection}
                        referenceLabel={referenceLabel}
                        comparisonLabel={comparisonLabel}
                      />
                    </div>
                    <SectionReadout
                      series={data.series}
                      quality={data.alignment.quality}
                      cursorIndex={cursorIndex}
                      selection={selection}
                      referenceLabel={referenceLabel}
                      comparisonLabel={comparisonLabel}
                      onClearSelection={() => setSelection(null)}
                      id={readoutId}
                    />
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>

      {trackId && (
        <LapPickerModal
          isOpen={pickerRole !== null}
          onClose={closePicker}
          trackId={trackId}
          trackName={trackName}
          currentRef={pickerRole === "reference" ? refLap : cmpLap}
          otherRef={pickerRole === "reference" ? cmpLap : refLap}
          onSelect={handlePick}
        />
      )}
    </div>
  );
}
